import _ from 'lodash';

import { ChipProps } from 'bento';
import { NewPurchaseOrder } from 'state/handsell';
import { OptionGroupInfo } from 'components/ticket-groups/MiniPurchaseSummary';
import { core } from '@tock/coretypes';
import { didPurchasePriceIncreaseFromZero } from 'components/reservation/reservation-edit-modal/utils/purchasePriceComparisonUtil';

interface OptionInfo {
  amount: number;
  count: number;
  id: number;
  name: string;
  optionGroupName?: string;
  optionGroupOrdering?: number;
  ordering?: number;
}

interface OptionInfoMapping {
  [optionId: number]: Partial<OptionInfo>;
}

function mapOptionIdToOptionInfo(optionGroups: core.TicketOptionGroup[]): OptionInfoMapping {
  const optionInfoMapping: OptionInfoMapping = {};
  _.forEach(optionGroups, (optionGroup) => {
    _.forEach(optionGroup.ticketOption as core.TicketOption[], (option) => {
      optionInfoMapping[option.id] = {
        optionGroupName: optionGroup.name,
        optionGroupOrdering: optionGroup.ordering,
        ordering: option.ordering,
      };
    });
  });
  return optionInfoMapping;
}

export function getOptionGroupsForTicketGroup(ticketGroup: core.TicketGroup): OptionGroupInfo[] {
  return organizeOptionsByOptionGroup(
    ticketGroup.ticketType[0].optionGroup as core.TicketOptionGroup[],
    ticketGroup.option as core.Price[]
  );
}

export function organizeOptionsByOptionGroup(
  optionGroups: core.TicketOptionGroup[],
  options: core.Price[]
): OptionGroupInfo[] {
  const optionIdToOptionInfo = mapOptionIdToOptionInfo(optionGroups);
  const flatOptions = _.map(options, (ticketOption: core.Price) => {
    const optionInfo = optionIdToOptionInfo[ticketOption.id];
    return _.omitBy(
      {
        id: ticketOption.id,
        amount: ticketOption.amount,
        count: 0,
        name: ticketOption.name,
        optionGroupName: optionInfo ? optionInfo.optionGroupName : undefined,
        optionGroupOrdering: optionInfo ? optionInfo.optionGroupOrdering : undefined,
        ordering: optionInfo ? optionInfo.ordering : undefined,
      },
      _.isUndefined
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any as OptionInfo;
  });
  const groupedOptions = _.groupBy(flatOptions, 'optionGroupName');
  return _.map(groupedOptions, (options) => {
    const anOption = options[0];
    if (!anOption.optionGroupName) {
      return {
        options,
      };
    }
    return {
      name: anOption.optionGroupName,
      options,
      ordering: anOption.optionGroupOrdering,
    };
  });
}

export function isPaidBooking(pastPurchase?: core.IPastPurchase, purchaseOrder?: NewPurchaseOrder) {
  const bookingRequiresPaymentCardHold = doesBookingRequirePaymentCardHold(
    purchaseOrder,
    pastPurchase
  );
  if (bookingRequiresPaymentCardHold) {
    return true;
  }

  if (
    purchaseOrder &&
    (!pastPurchase || purchaseOrder?.ticketTypeId !== pastPurchase.ticketTypeId)
  ) {
    return (purchaseOrder.totalPrice ?? 0) > 0 || (purchaseOrder.subtotal ?? 0) > 0;
  }

  return (pastPurchase?.amountDueCents ?? 0) > 0 || (pastPurchase?.amountPaidCents ?? 0) > 0;
}

export function doesBookingRequirePaymentCardHold(
  purchaseOrder: NewPurchaseOrder | undefined,
  pastPurchase?: core.IPastPurchase
) {
  // new booking or new experience
  if (
    purchaseOrder &&
    (!pastPurchase || purchaseOrder.ticketTypeId !== pastPurchase.ticketTypeId)
  ) {
    return (
      purchaseOrder.ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD' &&
      (purchaseOrder.ticketPriceInformation?.amountCents ?? 0) > 0
    );
  }

  if (
    purchaseOrder &&
    purchaseOrder.ticketPriceInformation?.priceType === 'PAYMENT_CARD_HOLD' &&
    didPurchasePriceIncreaseFromZero(pastPurchase, purchaseOrder)
  ) {
    return true;
  }

  // check for waived hold
  return !!pastPurchase?.paymentCardHold;
}

interface VarietyDisplayDetails {
  display: string;
  color: ChipProps['color'];
}

// TODO: Remove after SEATING_AREAS is launched fully
const oldVarietyMap: { [key: string]: VarietyDisplayDetails } = {
  DEPOSIT: { display: '$ DEP', color: 'success' },
  FULL: { display: '$ FULL', color: 'success' },
  RESERVATION: { display: 'FREE', color: 'primary' },
  DEPRECATED_EVENT: { display: 'EVENT', color: 'internal' },
  SEATED_EVENT: { display: 'SEATED EVENT', color: 'internal' },
  GA_EVENT: { display: 'GA EVENT', color: 'internal' },
  DEPRECATED_SPECIAL_TABLE: { display: 'SPECIAL', color: 'internal' },
  ORDER_DELIVERY: { display: 'ORDER_DELIVERY', color: 'success' },
  ORDER_PICKUP: { display: 'ORDER_PICKUP', color: 'success' },
  TABLESIDE: { display: 'TABLESIDE', color: 'success' },
};

export function getOldTicketTypeChipDisplayDetails(
  ticketTypeVariety: core.TicketType.Variety,
  isCCHold?: Boolean
) {
  if (isCCHold) {
    return { display: 'CC HOLD', color: 'primary' } as VarietyDisplayDetails;
  }

  return oldVarietyMap[ticketTypeVariety];
}

export function isUnassignedVisit(visit: core.IVisit | undefined | null): boolean {
  return visit?.tableId?.length === 0;
}
